import { useOAuthStore } from '@/features/auth/store';
import {
    checkOnboardingVoucher,
    isSnappyApiError,
    isSnappySessionExpiredError,
    redeemSnappyAdmission,
} from '@/lib/api/snappy';

import { useFlowFlagsStore } from './flagsStore';
import { useFlowStore } from './store';

/**
 * The launch-code rules every code input shares (the feature-flags view and
 * the V1 onboarding voucher step): a code ending in `V2` admits the social
 * identity to the browser wallet flow; any other code belongs to V1.
 */
export const UNLOCK_COPY = {
    invalid: "That code isn't valid.",
    signInFirst: 'Sign in with a social account first.',
    rateLimited: 'Too many tries. Wait a minute and try again.',
    unavailable: "Couldn't check the code right now. Try again.",
} as const;

export type RedeemV2Result = { ok: true } | { ok: false; message: string };

export function normalizeVoucherCode(code: string): string {
    return code.trim().toUpperCase();
}

/** factions' rule, on the normalized code: the suffix is the whole rule. */
export function isV2VoucherCode(code: string): boolean {
    return normalizeVoucherCode(code).endsWith('V2');
}

/** Plain copy for a refusal; the server's own text never reaches the user. */
export function unlockErrorMessage(error: unknown): string {
    if (isSnappySessionExpiredError(error)) return UNLOCK_COPY.signInFirst;
    if (isSnappyApiError(error)) {
        if (error.code?.toLowerCase().startsWith('voucher')) {
            return UNLOCK_COPY.invalid;
        }
        if (error.status === 429) return UNLOCK_COPY.rateLimited;
        if (
            error.status === 400 ||
            error.status === 404 ||
            error.status === 422
        ) {
            return UNLOCK_COPY.invalid;
        }
    }
    return UNLOCK_COPY.unavailable;
}

/**
 * Redeems a `V2` code for the signed-in social identity
 * (`POST /factions/speed/snappy/admission/redeem`). With the launch switch off
 * the redeem admits anyone without reading the code, so the code is then
 * checked on its own and must be valid. On success this browser remembers the
 * V2 flag for the account and the flow availability is re-read. Never throws;
 * switching flows is the caller's call.
 */
export async function redeemV2Code(code: string): Promise<RedeemV2Result> {
    const normalized = normalizeVoucherCode(code);
    try {
        const admission = await redeemSnappyAdmission(normalized);
        if (!admission.enabled) {
            const check = await checkOnboardingVoucher(normalized);
            if (!check.valid)
                return { ok: false, message: UNLOCK_COPY.invalid };
        }
    } catch (error) {
        return { ok: false, message: unlockErrorMessage(error) };
    }
    const me = useOAuthStore.getState().me;
    const accountHash = me?.authenticated ? (me.accountHash ?? null) : null;
    if (accountHash) useFlowFlagsStore.getState().markV2(accountHash);
    void useFlowStore.getState().refreshAvailability();
    return { ok: true };
}
